import {
  fetchMatterDetail,
  fetchMatterDetailList,
  getDocumentDetails,
  setDeleteDoc,
  updateDocumentDetail,
} from "../slice/MatterDetailsSlice";
import { fetchMyReviewsList, fetchSharedReviewsList, fetchVeteransAffairsList } from "../slice/HomeSlice";
import { fetchNotifications } from "../slice/Matterslice";
import { setFeatureFlags } from "../slice/FeatureFlagsSlice";
import {
  setAnnotationUpdate,
  triggerNotificationRefresh,
  setDocumentContentUpdate,
  setSocketConnected,
  markVaStatusUpdated,
  markPipelineDashboardUpdated,
} from "../slice/WebsocketSlice";
import { handleOptimizationEvent } from "../utils/uploadUtils";
import { editPDFsLock } from "../slice/PDFEditSlice";
import { addNotification } from "../slice/SnackbarSlice";
import { fetchNotificationAlerts } from "../slice/AlertsSlice";

let socket;
let previousMatterId = null;
let messageQueue = []; // Queue for messages sent before socket is open
let heartbeatInterval = null; // Heartbeat interval ID
let reconnectTimer = null; // Pending reconnect attempt
let reconnectAttempts = 0;

// A dropped socket used to leave the app silently stale until a reload,
// so reconnect indefinitely with backoff: pages like the Submit to VA
// ledger are left open for hours and their rows change server-side.
const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30000;

// Helper function to send queued messages (reduces nesting)
const sendQueuedMessages = () => {
  if (messageQueue.length === 0) {
    return;
  }
  
  console.log(`[WS] Sending ${messageQueue.length} queued messages`);
  for (const message of messageQueue) {
    socket.send(JSON.stringify(message));
    console.log("[WS] Queued message sent:", message);
  }
  messageQueue = []; // Clear the queue
};

export const websocketMiddleware = (storeAPI) => (next) => (action) => {
  // Handle sending messages through WebSocket
  if (action.type === 'websocket/sendMessage') {
    if (!socket) {
      // Socket not initialized yet, queue the message
      messageQueue.push(action.payload);
      console.log("[WS] Message queued (socket not initialized):", action.payload);
    } else if (socket.readyState === WebSocket.CONNECTING) {
      // Socket is connecting, queue the message
      messageQueue.push(action.payload);
      console.log("[WS] Message queued (socket connecting):", action.payload);
    } else if (socket.readyState === WebSocket.OPEN) {
      // Socket is open, send immediately
      const messageString = JSON.stringify(action.payload);
      socket.send(messageString);
      console.log("[WS] Message sent (raw JSON):", messageString);
      console.log("[WS] Message sent (parsed):", action.payload);
    } else {
      // Socket is closing or closed
      console.warn("[WS] Cannot send message - socket state:", socket.readyState);
    }
    return action; // Don't pass this action to reducers
  }

  // Let reducers run first so state updates
  const result = next(action);
  const state = storeAPI.getState();
  const matterId = state.websocket.currentMatterId;

  // Initialize socket once. While a reconnect is pending the backoff owns
  // reopening, otherwise any dispatched action would retry immediately.
  if (!socket && !reconnectTimer) {
    socket = new WebSocket(process.env.REACT_APP_WEBSOCKET_URL);

    socket.onopen = () => {
      console.log("[WS] Connected");
      reconnectAttempts = 0;

      // Read the matter here rather than closing over the one this socket was
      // created with: the user can open another matter while the socket is
      // still CONNECTING, and subscribing to the old one would leave the
      // client on the wrong subscription. Recording it as null when nothing
      // is open matters too, because carrying the dead socket's subscription
      // forward would make reopening that same matter look like no change,
      // so it would never be subscribed and its updates would stay silent.
      const openMatterId = storeAPI.getState()?.websocket?.currentMatterId;
      previousMatterId = openMatterId ?? null;
      if (openMatterId) {
        socket.send(JSON.stringify({ type: "subscribe", matter_id: openMatterId }));
        console.log("[WS] Subscribed to matter:", openMatterId);
      }
      
      // Start heartbeat/ping to keep connection alive
      startHeartbeat();

      // Send all queued messages
      sendQueuedMessages();

      // Announce the connection last. This dispatch re-enters the middleware,
      // which acts on the subscription state settled just above.
      storeAPI.dispatch(setSocketConnected(true));
    };

    socket.onclose = () => {
      console.warn("[WS] Disconnected");
      stopHeartbeat();
      socket = null;
      // Claim the backoff before dispatching anything. A dispatch re-enters
      // this middleware, and the pending-reconnect guard above is the only
      // thing stopping it from reopening the socket on the spot, which
      // against an unreachable server would be a tight reconnect loop.
      scheduleReconnect(storeAPI);
      storeAPI.dispatch(setSocketConnected(false));
    };
    
    socket.onerror = (error) => {
      console.error("[WS] Error", error);
      stopHeartbeat();
    };

    socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log("[WS] Message received:", message);

        switch (message.type) {
          case "document_lock_update":
            handleDocumentLockUpdate(message, storeAPI);
            break;
          case "sharepoint_document_update":
          case "submit_button_status_update":
          case "document_share_update":
            handleMatterListUpdate(message, storeAPI);
            break;

          case "folder_delete_update":
          case "folder_folder_delete":
          case "document_delete_update":
            handleSubmissionUpdate(storeAPI);
            handleReviewUpdate(storeAPI);
            handleNotificationUpdate(storeAPI);
            handleDeleteUpdate(message, storeAPI);
            break;

          case "document_submission_update":
            handleSubmissionUpdate(storeAPI);
            handleReviewUpdate(storeAPI);
            break;

          case "document_review_update":
            handleSubmissionUpdate(storeAPI);
            handleReviewUpdate(storeAPI);
            handleReviewDecisionAlert(message, storeAPI);
            break;

          case "assignment_update":
            handleAssignmentAlert(message, storeAPI);
            break;

          case "notification_settings_update":
            handleUpdateSettings(message, storeAPI);
            break;

          case "va_status_update":
            handleVeteransAffairsUpdate(storeAPI);
            break;

          case "folder_update":
          case "document_update":
            // matter-subscription based events
            handleTabViewUpdate(message, storeAPI);
            break;

          case "annotation_update":
            handleAnnotationUpdate(message, storeAPI);
            break;

            case "document_content_update":
              handleDocumentContentUpdate(message, storeAPI);
              break;
  
          case "annotation_change_confirmed":
            handleAnnotationChangeConfirmed(message, storeAPI);
            break;

          case "notification_update":
            handleNotificationUpdate(storeAPI);
            break;

          case "fax_received_alert":
            handleFaxReceivedAlert(message, storeAPI);
            break;

          case "pdf_optimization_update":
            handleOptimizationUpdate(message, storeAPI);
            break;

          case "pipeline_dashboard_update":
            // Signal only — the dashboard re-reads its own endpoint.
            storeAPI.dispatch(markPipelineDashboardUpdated(message.generation));
            break;

          case "connection_established":
            handleConnectionEstablished(message, storeAPI);
            break;

          case "error":
            handleWebSocketError(message, storeAPI);
            break;

          default:
            console.warn("[WS] Unknown message type:", message.type);
        }
      } catch (err) {
        console.error("[WS] Failed to parse message:", err);
      }
    };
  }

  // Handle matter change: unsubscribe previous, subscribe new
  if (matterId !== previousMatterId && socket?.readyState === WebSocket.OPEN) {
    // Unsubscribe from previous matter
    if (previousMatterId) {
      socket.send(JSON.stringify({ type: "unsubscribe", matter_id: previousMatterId }));
      console.log("[WS] Unsubscribed from matter:", previousMatterId);
    }

    // Subscribe to new matter
    if (matterId) {
      socket.send(JSON.stringify({ type: "subscribe", matter_id: matterId }));
      console.log("[WS] Subscribed to matter:", matterId);
    }

    previousMatterId = matterId;
  }

  return result;
};



function getLastNumber(url) {
  const match = url.split("/matter/")[1].split("/");
  return match;
}

const handleTabViewUpdate = (message, storeAPI) => {
  const state = storeAPI.getState();
  const currentMatterId = state.websocket.currentMatterId;
  const currentFolderId = state.websocket.currentFolderId;
  const docListingPage = state.matterDetails.docListingPage;

  if (!currentMatterId) return;

//   if(message.action==="create" && message.data.created_by === Number(localStorage.getItem("user_id"))) {
// return
//   }

  const { matter_id, action } = message;

  const { page_number, parent_folder_id } = message.data;

  if (!["create", "rename", "delete"].includes(action)) return;

  // Update sidebar if same matter
  if (String(matter_id) === String(currentMatterId)) {
    storeAPI.dispatch(fetchMatterDetail(currentMatterId));
    console.log("[WS] Sidebar updated for matter:", currentMatterId);
  }

  // Update doc listing if same matter + same folder
  if (
    String(matter_id) === String(currentMatterId) &&
    String(parent_folder_id) === String(currentFolderId) &&
    String(page_number) === String(docListingPage)
  ) {
    const payload = {
      id: currentMatterId,
      page: docListingPage,
      folder: parent_folder_id === "root" ? null : `F${parent_folder_id}`,
    };

    storeAPI.dispatch(fetchMatterDetailList(payload));
  }

};



const handleMatterListUpdate = (updated, storeAPI) => {
  const state = storeAPI.getState().matterDetails;
  const { docListingPage } = state;

  const id = getLastNumber(globalThis.location.pathname);
  if (String(id[0]) === String(updated?.matter_id)) {
    const matterId = updated?.matter_id;
    const documentId = updated?.document_id;

    storeAPI.dispatch(getDocumentDetails(documentId));
    let folderParam = null;
    if (id.at(-1)?.startsWith("F")) {
      folderParam = id.at(-1);
    }

    const payload = {
      id: matterId,
      page: docListingPage,
      ...(folderParam && { folder: folderParam }),
    };
    if(!(id.at(-1).startsWith("D"))){
        storeAPI.dispatch(fetchMatterDetailList(payload));
    }
        storeAPI.dispatch(fetchMatterDetail(matterId));
      }
    };

const handleSubmissionUpdate = (storeAPI) => {
if (global.location.pathname.includes('/home')) {
  console.log('URL contains /home');
storeAPI.dispatch(
    fetchMyReviewsList({
      page: 1,
      status: "",
      sort_direction: "asc",
      sort_by: "file_name",
      page_size: 10,
      search: "",
    })
  );
}

  
};

const handleReviewUpdate = (storeAPI) => {
  if (global.location.pathname.includes('/home')) {
  console.log('URL contains /home');
  
  storeAPI.dispatch(
    fetchSharedReviewsList({
      page: 1,
      status: "",
      sort_direction: "asc",
      sort_by: "file_name",
      page_size: 10,
      search: "",
    })
  );
}
};

const handleNotificationUpdate = (storeAPI) => {
  // Trigger notification refresh for both drawer and page
  storeAPI.dispatch(triggerNotificationRefresh());
  
  // Also fetch notifications for the drawer (default params)
  storeAPI.dispatch(
    fetchNotifications({
      page: 1,
      offset: 0,
      limit: 8,
    })
  );
};

const handleFaxReceivedAlert = (message, storeAPI) => {
  // The backend broadcasts fax alerts on the shared group with the
  // recipient list in user_ids; only surface the alert to the people
  // it was routed to (department notify list / ATTN-line match).
  const currentUserId = Number(localStorage.getItem("user_id"));
  const userIds = (message.user_ids || []).map(Number);
  if (!currentUserId || !userIds.includes(currentUserId)) {
    return;
  }

  // Refresh the bell (drawer + page) so the new FAX notification and
  // unread count appear without a reload.
  handleNotificationUpdate(storeAPI);

  // Instant toast so the fax is noticed even with the drawer closed.
  storeAPI.dispatch(
    addNotification({
      id: `${Date.now()}-fax-${message.intake_item_id ?? "unknown"}`,
      newMsg:
        message.message ||
        "New fax received — open Mail Intake to review it.",
      success: true,
    })
  );
};
/**
 * True when the signed-in user is one of the people this event is
 * addressed to. Everything broadcasts on one global `document_events`
 * group, so the backend names its intended recipients in the payload
 * and each client filters locally — the same contract
 * `fax_received_alert` uses with `user_ids`.
 */
const isAddressedToMe = (message) => {
  const currentUserId = Number(localStorage.getItem("user_id"));
  if (!currentUserId) return false;
  const targets = (message.notify_user_ids || []).map(Number);
  return targets.includes(currentUserId);
};

/**
 * Toast now, card from the server.
 *
 * The toast is the instant feedback. The banner card is deliberately
 * not synthesized locally: the websocket payload has no notification
 * id, so a local card could not be dismissed durably and would be
 * resurrected by any refetch that landed after the user cleared it.
 * These events are sent after commit, so the row is already readable
 * and one refetch yields a real card with a real id, correct counts and
 * the server's cap applied.
 */
const raiseAlert = (storeAPI, toast) => {
  storeAPI.dispatch(addNotification(toast));
  storeAPI.dispatch(fetchNotificationAlerts());
  handleNotificationUpdate(storeAPI);
};

const handleReviewDecisionAlert = (message, storeAPI) => {
  if (!isAddressedToMe(message)) return;

  const isApproved = message.review_action === "approved";
  const docName = message.document_name || "your document";
  const actor = message.reviewer_name || "A reviewer";

  // Transient toast so it registers immediately even if the user is
  // deep in another page.
  raiseAlert(storeAPI, {
    id: `${Date.now()}-review-${message.document_id}`,
    newMsg: isApproved
      ? `${actor} approved "${docName}"`
      : `${actor} sent "${docName}" back for rework`,
    success: isApproved,
  });
};

const handleAssignmentAlert = (message, storeAPI) => {
  if (!isAddressedToMe(message)) return;

  const docName = message.document_name || "a document";
  const actor = message.assigned_by_name || "A teammate";

  // The backend addresses each action to whoever it creates work for:
  // the assignee on 'assigned' and 'reopened', the assigner on
  // 'completed'. A reopen means the deliverable went back for rework,
  // so it is emphatically not a success.
  const COPY = {
    assigned: {
      newMsg: `${actor} assigned you work on "${docName}"`,
      success: true,
    },
    reopened: {
      newMsg: `Assignment reopened: "${docName}" went back for rework`,
      success: false,
    },
    completed: {
      newMsg: `Assignment complete: "${docName}"`,
      success: true,
    },
  };
  const copy = COPY[message.action];
  if (!copy) return;

  raiseAlert(storeAPI, {
    id: `${Date.now()}-assignment-${message.assignment_id}`,
    ...copy,
  });
};

const handleVeteransAffairsUpdate = (storeAPI) => {
  storeAPI.dispatch(
    fetchVeteransAffairsList()
  );
  // The ledger refetch above does not reach an open evidence modal, which
  // reads a single submission from its own endpoint.
  storeAPI.dispatch(markVaStatusUpdated());
};
const handleUpdateSettings = (data, storeAPI) => {
  const updatedFlags = {
    isExternalShareEnabled: data.email_external_share,
    isESignEnabled: data.email_signature_request,
    isRemindEmailEnabled: data.email_signature_reminder,
  };

  storeAPI.dispatch(setFeatureFlags(updatedFlags));
};

const handleDeleteUpdate = (data, storeAPI) => {
  console.log("updated",data)
  const userId = Number(localStorage.getItem("user_id"));
  const id = getLastNumber(globalThis.location.pathname);
  console.log("updated",userId,id[0])
  if (String(id[0]) === String(data.matter_id)) {
    if(userId === data.deleted_by){
      return
    }
  if(data.document_id){
    storeAPI.dispatch(setDeleteDoc(`D${data.document_id}`))
  } else if(data.folder_id){
    storeAPI.dispatch(setDeleteDoc(`F${data.folder_id}`))
  }
}
}

const handleAnnotationUpdate = (message, storeAPI) => {
  console.log("[WS] Annotation update received:", message);
  // Store the annotation update in Redux so components can access it
  storeAPI.dispatch(setAnnotationUpdate({
    document_id: message.document_id,
    user_id: message.user_id,
    user_name: message.user_name,
    xfdf_command: message.xfdf_command,
    timestamp: message.timestamp,
  }));
};

const handleWebSocketError = (message, storeAPI) => {
  console.error("[WS] ❌ Error from server:", message.message || message);
  console.error("[WS] Full error object:", JSON.stringify(message, null, 2));
  
  // If it's an authentication error, log helpful info
  if (message.message?.includes("authentication")) {
    console.warn("[WS] ⚠️ Authentication issue detected. Check:");
    console.warn("  1. Is user_id included in the message?");
    console.warn("  2. Does the backend need 'user' instead of 'user_id'?");
    console.warn("  3. Does the backend need a token or session_id?");
    console.warn("  4. Check Network tab → WS → Messages to see what was sent");
  }
};

const handleDocumentContentUpdate = (message, storeAPI) => {
  console.log("[WS] Document content update received:", message);
  // Store the document content update in Redux so components can access it
  storeAPI.dispatch(setDocumentContentUpdate({
    document_id: message.document_id,
    user_id: message.user_id,
    user_name: message.user_name,
    change_type: message.change_type,
    timestamp: message.timestamp || Date.now(),
  }));
};

const handleAnnotationChangeConfirmed = (message, storeAPI) => {
  console.log("[WS] ✅ Annotation change confirmed:", {
    document_id: message.document_id,
    timestamp: message.timestamp,
  });
  // You can dispatch an action here if you want to show a confirmation to the user
};

const handleDocumentLockUpdate = async (message, storeAPI) => {
  console.log("[WS] 🔒 Document lock update received:", message);
  
  const documentId = message.document_id || message.documentId || message.id;
  if (!documentId) {
    console.warn("[WS] ⚠️ Document lock update received without document_id:", message);
    return;
  }


  const state = storeAPI.getState();
  const currentUserId = Number(localStorage.getItem("user_id"));
  
  // Get current document detail from Redux
  const documentDetail = state.matterDetails.documentDetail;
  
  // Check if this document is currently open
  // We check by comparing documentDetail.id with the documentId from the message
  const isDocumentOpen = String(documentDetail?.id) === String(documentId) ||
                         documentDetail?.unique_id === `D${documentId}` ||
                         String(documentDetail?.id) === String(documentId);
  
  // Also check URL path as a fallback
  const currentPath = globalThis.location?.pathname || "";
  const isDocumentInPath = currentPath.includes(`/D${documentId}`) || 
                          currentPath.includes(`/${documentId}`);
  
  // Check if the document was previously locked by current user (to detect if we just unlocked it)
  const previousLockedBy = documentDetail?.locked_by;
  const wasLockedByCurrentUser = previousLockedBy === currentUserId;
  
  // Document is considered "open" only if it's in documentDetail AND in the URL path
  // This ensures we don't auto-lock documents that are being closed
  const documentIsOpen = isDocumentOpen && isDocumentInPath;
  
  console.log("[WS] 🔒 Document lock update:", {
    documentId,
    is_locked: message.is_locked,
    locked_by: message.locked_by,
    previousLockedBy,
    wasLockedByCurrentUser,
    currentUserId,
    documentIsOpen,
    isDocumentOpen,
    isDocumentInPath,
    documentDetailId: documentDetail?.id,
  });

  // Update document details in Redux — but ONLY when this event is about the
  // document currently loaded into `documentDetail`. Every lock/unlock event
  // firm-wide broadcasts on the same shared `document_events` group (see
  // consumers.py), so without this guard a lock event for a document THIS
  // user has never opened (e.g. a colleague opening their own file elsewhere)
  // was unconditionally merged into `documentDetail`, overwriting the lock
  // banner for whatever document this user actually has open. That produced
  // exactly the bug Matthew reported: "Patrick Randall is editing this file"
  // appearing on a document Patrick never touched. The same missing guard
  // could just as easily clear a real lock (an unrelated unlock event wiping
  // this document's `is_locked`), so gating the whole dispatch fixes both
  // directions rather than special-casing one.
  if (isDocumentOpen) {
    storeAPI.dispatch(updateDocumentDetail({
      is_locked: message.is_locked,
      locked_by: message.locked_by,
      locked_by_user: message.locked_by_user || message.locked_by_name,
    }));
  }


  const documentIdfromUrl = getLastNumber(globalThis.location.pathname)
  if(documentId === documentIdfromUrl.at(-1)){
 storeAPI.dispatch(getDocumentDetails(documentId));
  }
  // Refresh document details to get latest state
 

  // Check if the unlock was triggered by the current user (to avoid auto-locking after we just unlocked)
  // We check if the document was previously locked by current user AND is now unlocked
  const isUnlockedByCurrentUser = wasLockedByCurrentUser && (message.is_locked === false || message.is_locked === null);
  
  // Also check if the message indicates it was unlocked by current user (if message contains user_id)
  const messageUnlockedByCurrentUser = message.user_id === currentUserId && (message.is_locked === false || message.is_locked === null);
  
  // If document was unlocked and current user has it open, automatically lock it
  // BUT: Don't auto-lock if we just unlocked it ourselves (to prevent unwanted lock after unlock)
  // AND: Only auto-lock if document is actually open (both in Redux and URL path)
  if (documentIsOpen && (message.is_locked === false || message.is_locked === null) && !isUnlockedByCurrentUser && !messageUnlockedByCurrentUser) {
    // Document is now unlocked and we have it open - lock it for current user
    // Only do this if we didn't just unlock it ourselves
    console.log("[WS] 🔒 Document unlocked via WebSocket, auto-locking for current user...");
    try {
      const lockResult = await storeAPI.dispatch(editPDFsLock(documentId));
      if (lockResult.payload?.status === 200) {
        console.log("[WS] ✅ Document auto-locked successfully for user", currentUserId);
        // Update documentDetail with new lock info
        storeAPI.dispatch(updateDocumentDetail({
          is_locked: true,
          locked_by: lockResult.payload?.data?.locked_by || currentUserId,
        }));
        // Refresh document details
        storeAPI.dispatch(getDocumentDetails(documentId));
      }
    } catch (error) {
      console.error("[WS] ❌ Failed to auto-lock document:", error);
    }
  } else if (isUnlockedByCurrentUser || messageUnlockedByCurrentUser) {
    // We just unlocked it ourselves, don't auto-lock
    console.log("[WS] 🔓 Document was unlocked by current user, skipping auto-lock");
  } else if (documentIsOpen && message.is_locked === true && message.locked_by !== currentUserId) {
    // Document is locked by another user - ensure view-only mode
    console.log("[WS] 🔒 Document locked by another user, ensuring view-only mode");
    // The view-only mode is handled in the Document component based on documentDetail.locked_by
    // We've already updated documentDetail above, so the component will react to the change
  }

  // Also update matter list if needed (similar to handleMatterListUpdate)
  // Only call fetchMatterDetail if lock/unlock was done by a different user
  const isLockedByCurrentUser = message.locked_by === currentUserId;
  const wasUnlockedByCurrentUser = isUnlockedByCurrentUser || messageUnlockedByCurrentUser;
  const isActionByDifferentUser = !isLockedByCurrentUser && !wasUnlockedByCurrentUser;
  
  const id = getLastNumber(globalThis.location.pathname);
  if (String(id[0]) === String(message?.matter_id)) {
    const matterId = message?.matter_id;
    const { docListingPage } = state.matterDetails;
    let folderParam = null;
    if (id.at(-1)?.startsWith("F")) {
      folderParam = id.at(-1);
    }

    const payload = {
      id: matterId,
      page: docListingPage,
      ...(folderParam && { folder: folderParam }),
    };
    if (!(id.at(-1)?.startsWith("D"))) {
      storeAPI.dispatch(fetchMatterDetailList(payload));
    }
    // Only call fetchMatterDetail if lock/unlock was done by a different user
    if (isActionByDifferentUser) {
      storeAPI.dispatch(fetchMatterDetail(matterId));
    } else {
      console.log("[WS] 🔒 Lock/unlock by same user, skipping fetchMatterDetail");
    }
  }
};

const handleOptimizationUpdate = (message, storeAPI) => {
  console.log("[WS] 📦 Optimization event received:", message);
  
  // Extract document ID from the message
  // The message structure may vary, so we'll check multiple possible fields
  const documentId = message.document_id || message.documentId || message.id;
  
  if (!documentId) {
    console.warn("[WS] ⚠️ Optimization event received without document_id:", message);
    return;
  }
  
  console.log("[WS] 📦 Processing optimization for document ID:", documentId);
  
  // Call the optimization handler from uploadUtils
  handleOptimizationEvent(documentId);
};

const handleConnectionEstablished = (message, storeAPI) => {
  console.log("[WS] ✅ Connection established:", message.message);
  if (message.subscribed_events) {
    console.log("[WS] Available events:", message.subscribed_events);
  }
};

// Heartbeat/Ping functionality
const startHeartbeat = () => {
  // Clear any existing heartbeat
  stopHeartbeat();
  
  // Send ping every 30 seconds to keep connection alive
  heartbeatInterval = setInterval(() => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      const pingMessage = {
        type: 'ping',
        timestamp: Date.now(),
      };
      socket.send(JSON.stringify(pingMessage));
      // console.log("[WS] 💓 Heartbeat sent");
    }
  }, 30000); // 30 seconds
};

const stopHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
};

// Reconnect with exponential backoff. The dispatch is what reopens the
// socket: the middleware body owns creation, so it reads the matter
// subscription and everything else from current state rather than from a
// closure captured when the dead socket was created.
const scheduleReconnect = (storeAPI) => {
  if (reconnectTimer) {
    return;
  }

  const delay = Math.min(
    RECONNECT_BASE_DELAY_MS * 2 ** reconnectAttempts,
    RECONNECT_MAX_DELAY_MS
  );
  reconnectAttempts += 1;
  console.warn(`[WS] Reconnecting in ${delay}ms (attempt ${reconnectAttempts})`);

  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    storeAPI.dispatch({ type: "websocket/reconnect" });
  }, delay);
};

// Export function to send messages through WebSocket
export const sendWebSocketMessage = (message) => ({
  type: 'websocket/sendMessage',
  payload: message,
});